import {
  BaseAutoTracker,
  IAutoTrackerConfig,
  ICapturedEvent,
} from './BaseAutoTracker';
import { SelectorUtils } from './utils/SelectorUtils';
import { EventManager } from './utils/EventManager';
import { GomarkePixelConfig, PixelResponse } from '@/types';
import {
  IViewContentData,
  ViewContentTracker,
} from '@/conversion/tracks/viewContent';
import type { TrackConversionFn } from '../engine';

/**
 * Configuração específica para autoViewContent
 */
export interface IAutoViewContentConfig extends IAutoTrackerConfig {
  contentDataSelectors?: {
    content_id?: string;
    content_name?: string;
    content_type?: string;
    content_category?: string;
    value?: string;
  };
  customDataExtractor?: (element: Element) => Record<string, unknown>;
}

/**
 * AutoTracker para eventos de visualização de conteúdo
 */
export class AutoViewContentTracker extends BaseAutoTracker {
  private viewContentTracker: ViewContentTracker;
  private eventManager: EventManager;
  private autoConfig?: IAutoViewContentConfig;
  private trackConversion?: TrackConversionFn;
  private intersectionObserver?: IntersectionObserver;
  private trackedElements: WeakSet<Element> = new WeakSet();

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    visitorId: string = '',
    trackConversion?: TrackConversionFn
  ) {
    super(config, sessionId, version, visitorId);
    this.trackConversion = trackConversion;
    this.viewContentTracker = new ViewContentTracker(
      config,
      sessionId,
      this.visitorId,
      version
    );
    this.eventManager = new EventManager({
      debounceMs: 300,
      throttleMs: 1000,
      enableLogging: config.debug,
    });
  }

  public async initialize(autoConfig: IAutoViewContentConfig): Promise<void> {
    if (!autoConfig.isEnabled) {
      this.log('AutoViewContent desabilitado');
      return;
    }

    this.autoConfig = autoConfig;
    this.log(
      `Inicializando AutoViewContent com seletor: ${autoConfig.selector}`
    );

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () =>
        this.setupObservers()
      );
    } else {
      await this.setupObservers();
    }

    this.observeDOM(() => this.setupObservers());
    this.isInitialized = true;
  }

  private async setupObservers(): Promise<void> {
    if (!this.autoConfig) return;

    this.clearListeners();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }

    try {
      const elements = await SelectorUtils.findElementsWithRetry(
        this.autoConfig.selector,
        this.autoConfig.selectorType,
        3,
        1000
      );

      this.log(`Encontrados ${elements.length} elementos para ViewContent`);

      this.intersectionObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.5 &&
              !this.trackedElements.has(entry.target)
            ) {
              this.trackedElements.add(entry.target);
              const fakeEvent = new Event('view');
              void this.executeCallback(
                entry.target,
                fakeEvent,
                this.handleViewContent.bind(this),
                this.autoConfig!
              );
            }
          });
        },
        { threshold: [0.5] }
      );

      elements.forEach(element => {
        this.intersectionObserver?.observe(element as Element);
      });
    } catch (error) {
      this.log(`Erro ao configurar observer: ${String(error)}`);
    }
  }

  private async handleViewContent(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    this.log('ViewContent detectado', capturedEvent);
    const data = this.extractContentData(capturedEvent.element);

    const payload: IViewContentData = {
      ...data,
    };

    if (this.trackConversion) {
      return this.trackConversion('viewContent', payload);
    }
    return this.viewContentTracker.track(payload);
  }

  private extractContentData(element: Element): IViewContentData {
    const data: IViewContentData = {};
    const selectors = this.autoConfig?.contentDataSelectors;

    if (selectors?.content_id) {
      data.content_id = element.getAttribute(selectors.content_id) ?? undefined;
    }
    if (selectors?.content_name) {
      data.content_name =
        element.getAttribute(selectors.content_name) ??
        element.textContent?.trim() ??
        undefined;
    }
    if (selectors?.content_type) {
      data.content_type =
        element.getAttribute(selectors.content_type) ?? undefined;
    }
    if (selectors?.content_category) {
      data.content_category =
        element.getAttribute(selectors.content_category) ?? undefined;
    }
    if (selectors?.value) {
      data.value = this.parseNumber(element.getAttribute(selectors.value));
    }

    if (!data.content_id) {
      data.content_id = this.generateContentId(element);
    }

    if (!data.content_name) {
      data.content_name = element.getAttribute('title') ?? 'Conteudo';
    }

    if (this.autoConfig?.customDataExtractor) {
      Object.assign(data, this.autoConfig.customDataExtractor(element));
    }

    return data;
  }

  private generateContentId(element: Element): string {
    const position = SelectorUtils.getElementPosition(element);
    return `content_${Date.now()}_${Math.floor(position.top)}_${Math.floor(position.left)}`;
  }

  private parseNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const normalized = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  public stop(): void {
    this.clearListeners();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }
    this.eventManager.clear();
    this.isInitialized = false;
    this.log('AutoViewContent parado');
  }

  public async rebuild(): Promise<void> {
    if (this.autoConfig) {
      await this.setupObservers();
    }
  }

  public getViewContentStats(): {
    baseStats: unknown;
    eventStats: unknown;
  } {
    return {
      baseStats: {},
      eventStats: this.eventManager.getStats(),
    };
  }

  private log(message: string, data?: unknown): void {
    if (this.baseConfig.debug) {
      console.log(`[AutoViewContent] ${message}`, data);
    }
  }
}

// Função de conveniência para compatibilidade
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
export const autoViewContent = (_config: IAutoViewContentConfig): void => {
  console.warn(
    'autoViewContent: Use a classe AutoViewContentTracker diretamente'
  );
};
